import i18n from '@/i18n';
import * as Sentry from '@sentry/react';

type ErrorContext = 'generator' | 'scraper' | 'tts' | 'projects' | 'auth';

interface ErrorResult {
  title: string;
  description: string;
}

/**
 * Maps API errors to i18n-based user-friendly messages.
 * Uses error_code from backend when available, falls back to HTTP status.
 */
export function getErrorMessage(
  options: {
    status?: number;
    errorCode?: string;
    errorText?: string;
    context: ErrorContext;
    field?: string;
    env?: string;
  }
): ErrorResult {
  const t = (key: string, params?: Record<string, string>) => i18n.t(key, { ns: 'errors', ...params });
  const tc = (key: string) => i18n.t(key, { ns: 'common' });
  const { status, errorCode, errorText, context, field, env } = options;

  // Report to Sentry
  Sentry.captureException(
    new Error(`API Error [${context}]: ${errorCode || status || 'unknown'}${errorText ? ` - ${errorText}` : ''}`),
    {
      tags: { context, errorCode: errorCode || 'none', httpStatus: String(status || 'none') },
      level: status && status >= 500 ? 'error' : 'warning',
    }
  );

  // 1. Map specific error_code from backend
  if (errorCode) {
    const codeMap: Record<string, ErrorResult> = {
      // Generator
      PROJECT_NOT_FOUND: { title: t('generator.projectNotFound', { env: env || '?' }), description: t('generator.projectNotFoundHint') },
      RAG_COLLECTION_MISSING: { title: t('generator.ragCollectionMissing'), description: t('generator.projectNotFoundHint') },
      MISSING_FIELD: { title: t('generator.missingField', { field: field || '?' }), description: '' },
      // Scraper
      PROFILE_NOT_FOUND: { title: t('scraper.profileNotFound'), description: t('scraper.profileNotFoundHint') },
      APIFY_ERROR: { title: t('scraper.apifyError'), description: t('serverError') },
      API_KEY_MISSING: { title: t('scraper.apiKeyMissing'), description: '' },
      // TTS
      VOICE_NOT_FOUND: { title: t('tts.voiceNotFound'), description: '' },
      TEXT_TOO_LONG: { title: t('tts.textTooLong'), description: '' },
      QUOTA_EXCEEDED: { title: t('tts.quotaExceeded'), description: '' },
      // PromptLayer
      PROMPTLAYER_ERROR: { title: t('generator.promptLayerError'), description: t('generator.promptLayerErrorHint') },
    };

    if (codeMap[errorCode]) return codeMap[errorCode];
  }

  // 2. Map by HTTP status
  if (status) {
    if (status === 401) return { title: t('sessionExpired'), description: '' };
    if (status === 403) return { title: t('noPermission'), description: t('noPermissionApiKey') };
    if (status === 429) return { title: t('tooManyRequests'), description: '' };
    if (status === 400) return { title: t('invalidData', { field: field || '' }), description: '' };
    if (status === 404) {
      if (context === 'scraper') return { title: t('scraper.profileNotFound'), description: t('scraper.profileNotFoundHint') };
      if (context === 'generator') return { title: t('generator.projectNotFound', { env: env || '?' }), description: t('generator.projectNotFoundHint') };
    }
  }

  // 3. Context-specific fallback
  const contextFallbacks: Record<ErrorContext, string> = {
    generator: t('generator.generic'),
    scraper: t('scraper.apifyError'),
    tts: t('tts.generic'),
    projects: t('projects.externalApiError'),
    auth: t('auth.connectionError'),
  };

  return { title: contextFallbacks[context] || t('serverError'), description: '' };
}

/**
 * Detects network errors (no internet, DNS failure, timeout).
 */
export function isNetworkError(error: unknown): boolean {
  return error instanceof TypeError && (
    (error.message || '').toLowerCase().includes('fetch') ||
    (error.message || '').toLowerCase().includes('network') ||
    (error.message || '').toLowerCase().includes('failed')
  );
}

/**
 * Get a user-friendly message for network/catch errors.
 */
export function getNetworkErrorMessage(): ErrorResult {
  const t = (key: string) => i18n.t(key, { ns: 'errors' });
  Sentry.captureException(new Error('Network error detected'), { level: 'warning', tags: { context: 'network' } });
  return { title: t('networkError'), description: '' };
}
